import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  useTheme,
  LinearProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Unlocks, LessonProgress } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const MyCourses: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [unlocks, setUnlocks] = useState<Unlocks[]>([]);
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<LessonProgress[]>([]);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchMyCourses = async () => {
      try {
        setLoading(true);
        const [unlocksData, progressList] = await Promise.all([
          apiService.getMyUnlocks(),
          apiService.getMyProgress()
        ]);
        setUnlocks(unlocksData);
        setProgressData(progressList);
      } catch (error) {
        console.error('Error fetching my courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyCourses();
  }, [isAuthenticated, authLoading, navigate]);

  const getCourseProgress = (courseId: number, totalLessons?: number) => {
    if (!totalLessons || totalLessons === 0) return 0;
    
    // Find all progress records for lessons in this course
    // LessonProgress -> lesson -> course -> id
    const courseProgress = progressData.filter(p => p.lesson.course?.id === courseId && p.is_completed);
    const completedCount = courseProgress.length;
    
    return Math.round((completedCount / totalLessons) * 100);
  };

  if (authLoading || loading) {
    return <LoadingSpinner message="Loading your courses..." />;
  }

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumb />
        
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ mb: 6 }}
        >
          My Courses
        </Typography>

        {unlocks.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
            {unlocks.map((unlock) => {
              const progressPercentage = getCourseProgress(unlock.course.id, unlock.course.lesson_count);
              
              return (
                <Box key={unlock.id} sx={{ flex: '1 1 350px', maxWidth: '400px' }}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8],
                      },
                    }}
                    onClick={() => navigate(`/courses/${unlock.course.hashed}`)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={unlock.course.image}
                      alt={unlock.course.name}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Chip 
                          label={unlock.course.category.category} 
                          size="small" 
                          color="success"
                          variant="outlined"
                        />
                        <Chip 
                          label="Enrolled" 
                          size="small" 
                          color="success"
                        />
                      </Box>
                      
                      <Typography variant="h6" component="h3" gutterBottom>
                        {unlock.course.name}
                      </Typography>
                      
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                      >
                        {unlock.course.description}
                      </Typography>

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Progress
                          </Typography>
                          <Typography variant="caption" color="text.primary" fontWeight="bold">
                            {progressPercentage}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={progressPercentage} 
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#10b981',
                              borderRadius: 3,
                            }
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mt: 'auto' }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${unlock.course.hashed}`);
                          }}
                          sx={{
                            background: 'linear-gradient(135deg, #10b981 0%, #22c55e 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #059669 0%, #16a34a 100%)',
                            },
                          }}
                        >
                          Continue Learning
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No enrolled courses yet
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Start your learning journey by enrolling in one of our courses.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/courses')}
            >
              Browse Courses
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MyCourses;